'use client'

import React from 'react'
import Link from 'next/link'
import type { Route } from 'next'
import { useTranslation } from 'react-i18next'
import { cn } from '@kiao-voice/ui-kit'
import { trackSiteLinkClicked } from '../../lib/analytics'
import type { ReleaseStatus } from '../../lib/latest-release'
import { DownloadButton } from '../landing/download-button'
import { LanguageToggle } from '../landing/language-toggle'
import { StoreButton } from '../landing/store-button'
import { BrandLogo } from '../brand-logo'

export interface SiteFooterProps {
  release?: ReleaseStatus
  fallbackUrl?: string
  windowsRelease?: ReleaseStatus
  windowsFallbackUrl?: string
  /** false 時 Windows 那一列換成「準備中」佔位，不是下載連結。 */
  windowsDownloadEnabled?: boolean
}

/**
 * 還沒上線的頁面。`href` 是 `null` 就整條藏起來；整欄都還沒上線時整欄消失
 * （不要空欄，也不要「即將推出」的殘骸）。
 */
interface FooterLink {
  labelKey: string
  href: Route | null
}

const PRODUCT_LINKS: FooterLink[] = [
  { labelKey: 'site.footer.linkDictation', href: '/dictation' },
  { labelKey: 'site.footer.linkTranscription', href: '/transcription' },
  { labelKey: 'site.footer.linkMeetingNotes', href: '/meeting-notes' },
  { labelKey: 'site.footer.linkMcp', href: '/mcp-connector' },
]

const PLAN_LINKS: FooterLink[] = [
  { labelKey: 'site.footer.linkBlog', href: '/blog' },
  { labelKey: 'site.footer.linkPricing', href: '/pricing' },
  { labelKey: 'site.footer.linkReferral', href: '/referral' },
]

// 公司頁：關於我們與幫助中心還沒上線，填上 `href` 就會自己出現。
const COMPANY_LINKS: FooterLink[] = [
  { labelKey: 'site.footer.linkAbout', href: null },
  { labelKey: 'site.footer.linkHelp', href: null },
  { labelKey: 'site.footer.linkPrivacy', href: '/privacy' },
  { labelKey: 'site.footer.linkTerms', href: '/terms' },
]

const DOWNLOAD_BUTTON_CLASS =
  'w-full min-w-[170px] justify-center whitespace-normal text-center leading-[1.35]'

/**
 * 行銷站頁尾：品牌欄吃左側空間，連結欄依內容寬度靠右，下載是四顆等寬描邊按鈕，
 * 底列放版權、聯絡、語言切換。
 *
 * `<footer>` 是整頁唯一的 `contentinfo` landmark；每個連結欄由一個 `<h2>` 命名
 * （視覺上是 mono 微標），所以連結群組對 SEO 與螢幕閱讀器都是真的標題。這些
 * `<h2>` 雖然是 mono 字面，但字型 subset 測試把所有 `<h2>` 都當 display 文字，
 * 所以改欄標之後要重跑 `font:subset`。
 */
export function SiteFooter({
  release = null,
  fallbackUrl,
  windowsRelease = null,
  windowsFallbackUrl,
  windowsDownloadEnabled = true,
}: SiteFooterProps) {
  const { t } = useTranslation(['web'])

  const column = (titleKey: string, links: FooterLink[]) => {
    const live = links.filter((link) => link.href !== null)
    if (live.length === 0) return null
    return (
      <div className="min-w-[120px] flex-[0_1_auto] max-[920px]:flex-[1_1_140px]">
        <h2 className="mb-3.5 font-mono text-2xs font-medium tracking-[0.16em] text-muted uppercase">
          {t(`web:${titleKey}`)}
        </h2>
        <ul className="m-0 flex list-none flex-col gap-[11px] p-0">
          {live.map((link) =>
            link.href ? (
              <li key={link.labelKey}>
                <Link
                  href={link.href}
                  onClick={() => trackSiteLinkClicked('footer', link.href as string)}
                  className="text-base text-muted transition-colors hover:text-foreground"
                >
                  {t(`web:${link.labelKey}`)}
                </Link>
              </li>
            ) : null
          )}
        </ul>
      </div>
    )
  }

  return (
    <footer className="border-t border-border bg-background pt-(--gap-xl) pb-(--gap-lg) text-sm text-muted">
      <div className="mx-auto w-full max-w-(--container) px-5 min-[561px]:px-(--gutter)">
        <div className="flex flex-wrap items-start gap-x-(--gap-xl) gap-y-(--gap-lg)">
          <div className="min-w-[200px] flex-[1_1_240px] max-[920px]:flex-[1_1_100%]">
            <BrandLogo className="block h-[26px] w-auto max-w-full" />
            <p className="mt-3.5 text-md text-foreground">{t('web:site.footer.tagline')}</p>
            <p className="mt-2.5 text-sm leading-[1.55] text-muted">{t('web:site.footer.note')}</p>
          </div>

          {column('site.footer.colProduct', PRODUCT_LINKS)}
          {column('site.footer.colPlans', PLAN_LINKS)}

          <div className="min-w-[120px] flex-[0_1_auto] max-[920px]:flex-[1_1_140px]">
            <h2 className="mb-3.5 font-mono text-2xs font-medium tracking-[0.16em] text-muted uppercase">
              {t('web:site.footer.colDownload')}
            </h2>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              <li>
                <DownloadButton
                  release={release}
                  fallbackUrl={fallbackUrl}
                  variant="secondary"
                  size="compact"
                  className={DOWNLOAD_BUTTON_CLASS}
                  location="footer"
                />
              </li>
              <li>
                <DownloadButton
                  release={windowsRelease}
                  fallbackUrl={windowsFallbackUrl}
                  platform="windows"
                  variant="secondary"
                  size="compact"
                  className={DOWNLOAD_BUTTON_CLASS}
                  location="footer"
                  comingSoon={!windowsDownloadEnabled}
                />
              </li>
              <li>
                <StoreButton
                  platform="android"
                  variant="secondary"
                  size="compact"
                  className={DOWNLOAD_BUTTON_CLASS}
                  location="footer"
                />
              </li>
              <li>
                <StoreButton
                  platform="ios"
                  variant="secondary"
                  size="compact"
                  className={DOWNLOAD_BUTTON_CLASS}
                  location="footer"
                />
              </li>
            </ul>
          </div>

          {column('site.footer.colCompany', COMPANY_LINKS)}
        </div>

        <div
          className={cn(
            'mt-(--gap-xl) flex flex-wrap items-center gap-(--gap-md) border-t border-border pt-(--gap-md)',
            'max-[560px]:flex-col max-[560px]:items-start max-[560px]:gap-3.5'
          )}
        >
          <span className="font-mono text-xs text-muted">{t('web:site.footer.copyright')}</span>
          {/* /contact 而不是裸 mailto：mailto 只有在使用者的系統設好郵件處理程式
              時才有用，而連結文字本身已經帶了地址給想自己複製的人。 */}
          <Link
            href="/contact"
            onClick={() => trackSiteLinkClicked('footer', '/contact')}
            className="border-b border-transparent text-muted transition-colors hover:border-foreground hover:text-foreground"
          >
            {t('web:site.footer.contact')}
          </Link>
          <span className="flex-1 max-[560px]:hidden" />
          <LanguageToggle />
        </div>
      </div>
    </footer>
  )
}
